import { useTranslation } from "react-i18next";
import {
  Divider,
  Hidden,
  Stack,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveAltIcon from "@mui/icons-material/SaveAlt";

import { toSpanishDate } from "../../../helpers/dateFormat";
import { numberFormat } from "../../../helpers/numberFormat";
import {
  createAllInvestmentLpByStartup,
  createSheetLP,
  exportToExcelWithTabs,
} from "../../../helpers/exportToExcel";
import { totals } from "../../helpers/columns/columnsPosition";

export const LPTableSmall = ({
  percentage,
  generalSheet,
  distributionList,
}) => {
  const { t } = useTranslation("global");

  const distributionListByStartup = distributionList.reduce((acc, obj) => {
    const alias = obj?.alias;
    const existingStartup = acc.find((s) => s.startup === alias);
    if (existingStartup) {
      existingStartup.data.push(obj);
    } else {
      acc.push({ startup: alias, data: [obj], id: alias, url: obj?.url });
    }
    return acc;
  }, []);

  const totalsArray = totals(distributionListByStartup, percentage); // return array whit all startup sum
  const totalData = [
    {
      amount: numberFormat(
        totalsArray.reduce((acc, row) => acc + (Number(row.amount) || 0), 0).toFixed(2)
      ),
      fundValue: numberFormat(
        totalsArray.reduce((acc, row) => acc + (Number(row.fundValue) || 0), 0).toFixed(2)
      ),
      LPCost: numberFormat(
        totalsArray.reduce((acc, row) => acc + (Number(row.LPCost) || 0), 0).toFixed(2)
      ),
      LPValue: numberFormat(
        totalsArray.reduce((acc, row) => acc + (Number(row.LPValue) || 0), 0).toFixed(2)
      ),
      startup: "Totales Latentes",
      url: "",
      id: "Totales Latentes",
    },
  ]; // totals rows

  const results = totalData.concat(distributionListByStartup); // add totals rows in distributionListByStartup

  const startupSheet = createAllInvestmentLpByStartup(
    [
      "Startup",
      t("lptable.fundCost"),
      t("lptable.fundValue"),
      t("lptable.LPCost"),
      t("lptable.LPValue"),
    ],
    results,
    generalSheet?.columns[2],
    percentage
  );
  const generalStartupSheet = [generalSheet, startupSheet];
  const columnsToExcel = [
    t("lptable.investmentDate"),
    t("lptable.round"),
    t("lptable.investmentType"),
    t("lptable.fundCost"),
    t("lptable.fundValue"),
    t("lptable.LPCost"),
    t("lptable.LPValue"),
  ];
  const startupDetailSheet = createSheetLP(results, columnsToExcel, percentage);
  const finalSheets = [...generalStartupSheet, ...startupDetailSheet];

  return (
    <Hidden mdUp>
      <Stack alignItems="flex-end">
        <Button
          onClick={() => exportToExcelWithTabs(finalSheets, "inversiones_LP")}
          color="secondary"
          startIcon={<SaveAltIcon />}
        >
          {t("toExcel.export")}
        </Button>
      </Stack>
      {distributionList !== "isLoading" && distributionListByStartup && (
        <>
          {distributionListByStartup.map((fund) => {
            const fundValue = () => {
              const totalValue = fund.data.reduce((acc, obj) => {
                const shares = obj.amount / obj.participationValuation;
                const value = (obj.participationValuation && obj.lastValoration !== null && obj.lastValoration !== undefined)
                  ? obj.lastValoration * shares
                  : obj.amount;
                return acc + value;
              }, 0);
              return numberFormat(totalValue.toFixed(2));
            };
            const LPCost = () => {
              const value = fund.data.reduce((acc, obj) => {
                return acc + obj.amount * (percentage / 100);
              }, 0);
              return numberFormat(value.toFixed(2));
            };
            const LPValue = () => {
              const totalValue = fund.data.reduce((acc, obj) => {
                const shares =
                  (obj.amount * (percentage / 100)) /
                  obj.participationValuation;
                const value = (obj.participationValuation && obj.lastValoration !== null && obj.lastValoration !== undefined)
                  ? obj.lastValoration * shares
                  : obj.amount * (percentage / 100);
                return acc + value;
              }, 0);
              return numberFormat(totalValue.toFixed(2));
            };
            return (
              <Accordion key={fund.id}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    justifyContent="space-between"
                  >
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      md={{ justifyContent: "space-between" }}
                      marginRight={1}
                    >
                      <Typography
                        variant="caption"
                        color="primary"
                        marginRight="10px"
                      >
                        Startup:
                      </Typography>
                      <Typography variant="caption" color="secondary">
                        {fund.startup}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      md={{ justifyContent: "space-between" }}
                      marginRight={1}
                    >
                      <Typography
                        variant="caption"
                        color="primary"
                        marginRight="10px"
                      >
                        {t("lptable.fundCost")}:
                      </Typography>
                      <Typography variant="caption" color="secondary">
                        {numberFormat(
                          fund.data.reduce((acc, obj) => acc + obj.amount, 0)
                        )}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      md={{ justifyContent: "space-between" }}
                      marginRight={1}
                    >
                      <Typography
                        variant="caption"
                        color="primary"
                        marginRight="10px"
                      >
                        {t("lptable.fundValue")}:
                      </Typography>
                      <Typography variant="caption" color="secondary">
                        {fundValue()}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      md={{ justifyContent: "space-between" }}
                      marginRight={1}
                    >
                      <Typography
                        variant="caption"
                        color="primary"
                        marginRight="10px"
                      >
                        {t("lptable.LPCost")}:
                      </Typography>
                      <Typography variant="caption" color="secondary">
                        {LPCost()}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      md={{ justifyContent: "space-between" }}
                      marginRight={1}
                    >
                      <Typography
                        variant="caption"
                        color="primary"
                        marginRight="10px"
                      >
                        {t("lptable.LPValue")}:
                      </Typography>
                      <Typography variant="caption" color="secondary">
                        {LPValue()}
                      </Typography>
                    </Stack>
                    {fund.url && (
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        md={{ justifyContent: "space-between" }}
                        marginRight={1}
                      >
                        <Typography
                          variant="caption"
                          color="primary"
                          marginRight="10px"
                        >
                          {t("opportunities.url")}:
                        </Typography>
                        <Typography variant="caption" color="secondary">
                          <a href={fund.url} target="_blank" rel="noreferrer">
                            {t("link.link")}
                          </a>
                        </Typography>
                      </Stack>
                    )}
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      md={{ justifyContent: "space-between" }}
                      marginRight={1}
                    ></Stack>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  {fund.data.map((invest) => {
                    const shares =
                      invest.amount / invest.participationValuation;
                    const sharesLP =
                      (invest.amount * (percentage / 100)) /
                      invest.participationValuation;
                    return (
                      <Stack key={invest.id}>
                        <Divider sx={{ margin: "10px 0" }} />
                        <Stack
                          width="100%"
                          direction="row"
                          gap={1}
                          key={invest.id}
                        >
                          <Stack
                            direction="row"
                            flexWrap="wrap"
                            justifyContent="space-between"
                          >
                            <Stack
                              direction="row"
                              flexWrap="wrap"
                              md={{ justifyContent: "space-between" }}
                              marginRight={1}
                            >
                              <Typography
                                variant="caption"
                                color="primary"
                                marginRight="10px"
                              >
                                {t("lptable.investmentDate")}{" "}
                              </Typography>
                              <Typography variant="caption" color="secondary">
                                {toSpanishDate(invest.investmentDate)}
                              </Typography>
                            </Stack>
                            <Stack
                              direction="row"
                              flexWrap="wrap"
                              md={{ justifyContent: "space-between" }}
                              marginRight={1}
                            >
                              <Typography
                                variant="caption"
                                color="primary"
                                marginRight="10px"
                              >
                                {t("lptable.investmentType")}{" "}
                              </Typography>
                              <Typography variant="caption" color="secondary">
                                {invest.investmentType === "Ampliación Capital"
                                  ? t(
                                      "opportunities.investmentTypeCapitalIncrease"
                                    )
                                  : t("opportunities.investmentTypeEquityLoan")}
                              </Typography>
                            </Stack>
                            <Stack
                              direction="row"
                              flexWrap="wrap"
                              md={{ justifyContent: "space-between" }}
                              marginRight={1}
                            >
                              <Typography
                                variant="caption"
                                color="primary"
                                marginRight="10px"
                              >
                                {t("lptable.fundCost")}{" "}
                              </Typography>
                              <Typography variant="caption" color="secondary">
                                {numberFormat(invest.amount)}
                              </Typography>
                            </Stack>
                            <Stack
                              direction="row"
                              flexWrap="wrap"
                              md={{ justifyContent: "space-between" }}
                              marginRight={1}
                            >
                              <Typography
                                variant="caption"
                                color="primary"
                                marginRight="10px"
                              >
                                {t("lptable.fundValue")}{" "}
                              </Typography>
                              <Typography variant="caption" color="secondary">
                                {(invest.participationValuation && invest.lastValoration !== null && invest.lastValoration !== undefined)
                                  ? numberFormat(
                                      (invest.lastValoration * shares).toFixed(
                                        2
                                      )
                                    )
                                  : numberFormat(invest.amount.toFixed(2))}
                              </Typography>
                            </Stack>
                            <Stack
                              direction="row"
                              flexWrap="wrap"
                              md={{ justifyContent: "space-between" }}
                              marginRight={1}
                            >
                              <Typography
                                variant="caption"
                                color="primary"
                                marginRight="10px"
                              >
                                {t("lptable.LPCost")}{" "}
                              </Typography>
                              <Typography variant="caption" color="secondary">
                                {numberFormat(
                                  (invest.amount * (percentage / 100)).toFixed(
                                    2
                                  )
                                )}
                              </Typography>
                            </Stack>
                            <Stack
                              direction="row"
                              flexWrap="wrap"
                              md={{ justifyContent: "space-between" }}
                              marginRight={1}
                            >
                              <Typography
                                variant="caption"
                                color="primary"
                                marginRight="10px"
                              >
                                {t("lptable.LPValue")}{" "}
                              </Typography>
                              <Typography variant="caption" color="secondary">
                                {/* {sharesLP !== Infinity
                                  ? numberFormat(
                                      (invest.lastValoration * shares).toFixed(
                                        2
                                      )
                                    )
                                  : "-"} */}
                                {(invest.participationValuation && invest.lastValoration !== null && invest.lastValoration !== undefined)
                                  ? numberFormat(
                                      (
                                        invest.lastValoration * sharesLP
                                      ).toFixed(2)
                                    )
                                  : numberFormat(
                                      (
                                        invest.amount *
                                        (percentage / 100)
                                      ).toFixed(2)
                                    )}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Stack>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </>
      )}
    </Hidden>
  );
};
